// ============================================================================
// HR DASHBOARD v4.0 — PROFESSIONAL COMPACT LAYOUT
// ============================================================================
// REDESIGN:
// 1. Compact summary cards (4 small cards: Total, Checked In, Late, Absent)
// 2. Map moved to RIGHT sidebar (compact, vertical orientation)
// 3. Charts arranged horizontally (Pie + Bar side by side)
// 4. Recent activity in right sidebar below map
// 5. Professional spacing and clean layout
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Progress,
  Space,
  Tag,
  Avatar,
  List,
  Spin,
  Alert,
  Badge,
  Button,
  Segmented,
  Tooltip,
} from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
  SyncOutlined,
  LeftOutlined,
  RightOutlined,
  DashboardOutlined,
  PieChartOutlined,
  BarChartOutlined,
  CalendarOutlined,
  GlobalOutlined,
  AimOutlined,
  LoginOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import {
  useAttendanceStats,
  useLocationStats,
  useAttendanceRecords,
} from '../../../hooks/queries/attendanceQueries';

import { polygonLocationsService, ProjectLocation } from '../../../services/polygonLocations.service';
import { attendanceService, CurrentCheckIn } from '../../../services/locationattendance.service';
import { employeesService, Employee } from '../../../services/ptsemployees.service';

import GoogleMapComponent from '../../../components/dashboard/sections/GoogleMapComponent';

import styles from './HrDashboard.module.css';

// ============================================================================
// TYPES
// ============================================================================
interface HrDashboardProps {
  onNavigate?: (page: string) => void;
}

interface StatusDataItem {
  name: string;
  value: number;
  color: string;
  [key: string]: string | number;
}

interface LocationChartDataItem {
  name: string;
  count: number;
  percentage: number;
  [key: string]: string | number;
}

interface AttendanceRecord {
  id: string;
  employeeName: string;
  realImage: string;
  checkIn: string;
  checkOut: string;
  status: string;
  normalizedLocation: string;
}

const COLORS = {
  present: '#52c41a',
  late: '#faad14',
  absent: '#ff4d4f',
  onLeave: '#1890ff',
  incomplete: '#ff7a45',
  primary: '#4A90E2',
  secondary: '#722ed1',
};

type FirebaseLoadState = 'idle' | 'loading' | 'loaded' | 'error';

// ============================================================================
// CUSTOM HOOK: useFirebaseData
// ============================================================================
function useFirebaseData() {
  const [projectLocations, setProjectLocations] = useState<ProjectLocation[]>([]);
  const [currentCheckIns, setCurrentCheckIns] = useState<CurrentCheckIn[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadState, setLoadState] = useState<FirebaseLoadState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const loadData = useCallback(async () => {
    setLoadState('loading');
    setError(null);
    try {
      const startTime = Date.now();
      const [locs, checks, emps] = await Promise.all([
        polygonLocationsService.getAllPolygonLocations(),
        attendanceService.getCurrentCheckIns(),
        employeesService.getAllEmployees(),
      ]);
      console.log(`✅ Firebase loaded in ${Date.now() - startTime}ms`);
      setProjectLocations(locs);
      setCurrentCheckIns(checks);
      setEmployees(emps);
      setLoadState('loaded');
    } catch (err) {
      console.error('❌ Firebase load failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setLoadState('error');
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData, retryCount]);

  useEffect(() => {
    if (loadState !== 'loaded') return;
    const unsub1 = polygonLocationsService.subscribeToPolygonLocations(setProjectLocations);
    const unsub2 = attendanceService.subscribeToCurrentCheckIns(setCurrentCheckIns);
    const unsub3 = employeesService.subscribeToEmployees(setEmployees);
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [loadState]);

  return {
    projectLocations, currentCheckIns, employees,
    loadState, error,
    retry: useCallback(() => setRetryCount(c => c + 1), []),
    isLoading: loadState === 'loading' || loadState === 'idle',
    isLoaded: loadState === 'loaded',
    isError: loadState === 'error',
  };
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const HrDashboard: React.FC<HrDashboardProps> = ({ onNavigate }) => {
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [mapView, setMapView] = useState<'satellite' | 'roadmap'>('satellite');
  const [selectedProject, setSelectedProject] = useState<ProjectLocation | null>(null);

  const firebase = useFirebaseData();

  const {
    data: stats, isLoading: statsLoading, isError: statsError, refetch: refetchStats,
  } = useAttendanceStats(selectedDate);

  const {
    data: locations, isLoading: locationsLoading, refetch: refetchLocations,
  } = useLocationStats(selectedDate);

  const {
    data: recordsData, isLoading: recordsLoading, refetch: refetchRecords,
  } = useAttendanceRecords({
    startDate: selectedDate, endDate: selectedDate, pageSize: 1000, sortOrder: 'desc',
  });

  const records = (recordsData?.records || []) as AttendanceRecord[];

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      refetchStats(); refetchLocations(); refetchRecords();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, refetchStats, refetchLocations, refetchRecords]);

  // ============================================================================
  // COMPUTED DATA
  // ============================================================================
  const loading = statsLoading || locationsLoading || recordsLoading;

  const totalCheckedIn = stats?.checkInCount || 0;
  const totalPresent = (stats?.present || 0) + (stats?.late || 0) + (stats?.incomplete || 0);

  const statusData = useMemo<StatusDataItem[]>(() => {
    if (!stats) return [];
    
    // ✅ FIX: "On Time" should include both "Present" (completed on time) 
    // and "Incomplete" (checked in on time but haven't checked out yet)
    // This is based on actual work timings - if they checked in on time, they're "On Time"
    const onTimeTotal = (stats.present || 0) + (stats.incomplete || 0);
    
    return [
      { name: 'On Time', value: onTimeTotal, color: COLORS.present },
      { name: 'Late', value: stats.late, color: COLORS.late },
      { name: 'Absent', value: stats.absent, color: COLORS.absent },
      { name: 'On Leave', value: stats.onLeave, color: COLORS.onLeave },
    ].filter(item => item.value > 0);
  }, [stats]);

  const locationChartData = useMemo<LocationChartDataItem[]>(() => {
    if (!locations) return [];
    return locations.map(loc => ({
      name: loc.normalizedName, count: loc.count, percentage: loc.percentage,
    }));
  }, [locations]);

  const recentCheckIns = useMemo(() => {
    return records
      .filter(r => r.checkIn && r.checkIn !== '--')
      .sort((a, b) => {
        const parseTime = (t: string) => {
          if (!t || t === '--') return 0;
          try {
            const today = dayjs(selectedDate);
            const [time, period] = t.split(' ');
            const [h, m] = time.split(':').map(Number);
            let h24 = h;
            if (period === 'PM' && h !== 12) h24 += 12;
            if (period === 'AM' && h === 12) h24 = 0;
            return today.hour(h24).minute(m).valueOf();
          } catch { return 0; }
        };
        return parseTime(b.checkIn) - parseTime(a.checkIn);
      })
      .slice(0, 10);
  }, [records, selectedDate]);

  const checkInRate = stats ? Math.round((totalCheckedIn / stats.totalEmployees) * 100) : 0;
  const checkOutRate = stats ? Math.round((stats.checkOutCount / stats.totalEmployees) * 100) : 0;

  const mapStats = useMemo(() => ({
    totalProjects: firebase.projectLocations.length,
    activeProjects: firebase.projectLocations.filter(p => p.isActive).length,
    totalCheckedIn: firebase.currentCheckIns.length,
    projectsWithEmployees: new Set(firebase.currentCheckIns.map(c => c.locationId)).size,
  }), [firebase.projectLocations, firebase.currentCheckIns]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleRefresh = () => { refetchStats(); refetchLocations(); refetchRecords(); };

  const handleDateChange = (dir: 'prev' | 'next' | 'today') => {
    if (dir === 'today') setSelectedDate(dayjs().format('YYYY-MM-DD'));
    else if (dir === 'prev') setSelectedDate(dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD'));
    else setSelectedDate(dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD'));
  };

  const handleMapLoad = useCallback(() => console.log('✅ Map loaded'), []);

  const isToday = dayjs(selectedDate).isSame(dayjs(), 'day');
  const isYesterday = dayjs(selectedDate).isSame(dayjs().subtract(1, 'day'), 'day');
  const shouldShowMap = firebase.isLoaded && firebase.projectLocations.length > 0;

  // ============================================================================
  // RENDER — LOADING
  // ============================================================================
  if ((loading || firebase.isLoading) && !stats && !firebase.isLoaded) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (statsError && firebase.isError) {
    return (
      <div className={styles.errorContainer}>
        <Alert
          message="Dashboard Unavailable"
          description={firebase.error || 'Check your connection.'}
          type="error"
          showIcon
          action={<Button type="primary" onClick={() => { handleRefresh(); firebase.retry(); }}>Retry</Button>}
        />
      </div>
    );
  }

  // ============================================================================
  // RENDER — MAIN
  // ============================================================================
  return (
    <div className={styles.dashboardContainer}>
      {/* ── COMPACT HEADER ── */}
      <div className={styles.dashboardHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.dashboardTitle}>
            <DashboardOutlined /> Phoenician Attendance Overview
          </h1>
          <div className={styles.dateDisplay}>
            <CalendarOutlined />
            <span className={styles.dateText}>
              {dayjs(selectedDate).format('ddd, DD MMM YYYY')}
            </span>
            {isToday && <Badge count="LIVE" style={{ backgroundColor: '#52c41a' }} />}
            {isYesterday && <Badge count="YESTERDAY" style={{ backgroundColor: '#faad14' }} />}
          </div>
        </div>
        <div className={styles.headerControls}>
          <Space size="small" wrap>
            <Button.Group size="small">
              <Button icon={<LeftOutlined />} onClick={() => handleDateChange('prev')} />
              <Button onClick={() => handleDateChange('today')} type={isToday ? 'primary' : 'default'}>Today</Button>
              <Button icon={<RightOutlined />} onClick={() => handleDateChange('next')} disabled={dayjs(selectedDate).add(1, 'day').isAfter(dayjs())} />
            </Button.Group>
            <Button icon={<ReloadOutlined spin={loading} />} onClick={handleRefresh} size="small" />
            <Tooltip title={autoRefresh ? 'Auto-refresh ON (30s)' : 'Auto-refresh OFF'}>
              <Button icon={<SyncOutlined spin={autoRefresh} />} onClick={() => setAutoRefresh(!autoRefresh)} type={autoRefresh ? 'primary' : 'default'} size="small" />
            </Tooltip>
          </Space>
        </div>
      </div>

      {/* ── COMPACT SUMMARY CARDS ── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6} lg={6}>
          <Card className={styles.summaryCardCompact} bordered={false} bodyStyle={{ padding: '12px 16px' }}>
            <div className={styles.compactContent}>
              <div className={styles.compactIcon} style={{ backgroundColor: 'rgba(24, 144, 255, 0.08)' }}>
                <TeamOutlined style={{ color: '#1890ff', fontSize: 18 }} />
              </div>
              <div className={styles.compactStats}>
                <div className={styles.compactValue}>{stats?.totalEmployees || 0}</div>
                <div className={styles.compactLabel}>Total</div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={6}>
          <Card className={styles.summaryCardCompact} bordered={false} bodyStyle={{ padding: '12px 16px' }}>
            <div className={styles.compactContent}>
              <div className={styles.compactIcon} style={{ backgroundColor: 'rgba(82, 196, 26, 0.08)' }}>
                <LoginOutlined style={{ color: '#52c41a', fontSize: 18 }} />
              </div>
              <div className={styles.compactStats}>
                <div className={styles.compactValue}>{totalCheckedIn}</div>
                <div className={styles.compactLabel}>Checked In</div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={6}>
          <Card className={styles.summaryCardCompact} bordered={false} bodyStyle={{ padding: '12px 16px' }}>
            <div className={styles.compactContent}>
              <div className={styles.compactIcon} style={{ backgroundColor: 'rgba(250, 173, 20, 0.08)' }}>
                <ClockCircleOutlined style={{ color: '#faad14', fontSize: 18 }} />
              </div>
              <div className={styles.compactStats}>
                <div className={styles.compactValue}>{stats?.late || 0}</div>
                <div className={styles.compactLabel}>Late</div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={6}>
          <Card className={styles.summaryCardCompact} bordered={false} bodyStyle={{ padding: '12px 16px' }}>
            <div className={styles.compactContent}>
              <div className={styles.compactIcon} style={{ backgroundColor: 'rgba(255, 77, 79, 0.08)' }}>
                <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
              </div>
              <div className={styles.compactStats}>
                <div className={styles.compactValue}>{stats?.absent || 0}</div>
                <div className={styles.compactLabel}>Absent</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── MAIN CONTENT: LEFT CHARTS + RIGHT MAP & ACTIVITY ── */}
      <Row gutter={[16, 16]}>
        {/* LEFT SIDE: CHARTS */}
        <Col xs={24} lg={16}>
          <Row gutter={[16, 16]}>
            {/* Status Distribution Chart */}
            <Col xs={24} xl={12}>
              <Card
                title={<div className={styles.cardTitle}><PieChartOutlined /><span>Status Distribution</span></div>}
                className={styles.chartCard}
                extra={<Tag color="blue">{dayjs(selectedDate).format('MMM DD')}</Tag>}
                bodyStyle={{ padding: '16px' }}
              >
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2} dataKey="value"
                        label={({ name, percent }) => `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`} labelLine={false}>
                        {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <RechartsTooltip formatter={(v, n) => [v, n]} />
                      <Legend verticalAlign="bottom" height={36} iconSize={10} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <div className={styles.noData}>No data</div>}
              </Card>
            </Col>

            {/* Top Locations Chart */}
            <Col xs={24} xl={12}>
              <Card
                title={<div className={styles.cardTitle}><BarChartOutlined /><span>Top Locations</span></div>}
                className={styles.chartCard}
                bodyStyle={{ padding: '16px' }}
              >
                {locationChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={locationChartData.slice(0, 8)} margin={{ top: 10, right: 10, left: -10, bottom: 45 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#8c8c8c', fontSize: 9 }}
                        angle={-40} textAnchor="end" height={55} interval={0} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#8c8c8c', fontSize: 10 }} />
                      <RechartsTooltip />
                      <Bar dataKey="count" fill={COLORS.primary} radius={[4, 4, 0, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className={styles.noData}>No data</div>}
              </Card>
            </Col>

            {/* Check-in/out Progress */}
            <Col xs={24}>
              <Card
                className={styles.progressCard}
                title={<div className={styles.cardTitle}><CheckCircleOutlined /><span>Today&apos;s Progress</span></div>}
                bodyStyle={{ padding: '16px 20px' }}
              >
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <div className={styles.progressItem}>
                      <div className={styles.progressHeader}>
                        <span className={styles.progressLabel}><LoginOutlined style={{ marginRight: 6 }} />Check-in Rate</span>
                        <span className={styles.progressValue}>{checkInRate}%</span>
                      </div>
                      <Progress percent={checkInRate} strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }} strokeWidth={10} showInfo={false} />
                      <div className={styles.progressStats}>{totalCheckedIn} / {stats?.totalEmployees || 0} employees</div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div className={styles.progressItem}>
                      <div className={styles.progressHeader}>
                        <span className={styles.progressLabel}><LogoutOutlined style={{ marginRight: 6 }} />Check-out Rate</span>
                        <span className={styles.progressValue}>{checkOutRate}%</span>
                      </div>
                      <Progress percent={checkOutRate} strokeColor={{ '0%': '#722ed1', '100%': '#eb2f96' }} strokeWidth={10} showInfo={false} />
                      <div className={styles.progressStats}>{stats?.checkOutCount || 0} / {stats?.totalEmployees || 0} employees</div>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>

        {/* RIGHT SIDE: MAP + RECENT ACTIVITY */}
        <Col xs={24} lg={8}>
          <Row gutter={[16, 16]}>
            {/* Compact Map */}
            <Col xs={24}>
              <Card
                className={styles.mapCardCompact}
                bodyStyle={{ padding: 0 }}
                title={
                  <div className={styles.cardTitle}>
                    <GlobalOutlined />
                    <span>Live Map</span>
                    {shouldShowMap && <Badge count="LIVE" style={{ backgroundColor: '#52c41a', marginLeft: 8, fontSize: 9 }} />}
                  </div>
                }
                extra={
                  <Segmented
                    size="small"
                    options={[
                      { label: 'Sat', value: 'satellite', icon: <GlobalOutlined /> },
                      { label: 'Map', value: 'roadmap', icon: <EnvironmentOutlined /> },
                    ]}
                    value={mapView}
                    onChange={v => setMapView(v as 'satellite' | 'roadmap')}
                  />
                }
              >
                <div className={styles.mapContainerCompact}>
                  {firebase.isLoading && (
                    <div className={styles.mapLoadingOverlay}>
                      <Spin size="default" /><p style={{ fontSize: 12 }}>Loading...</p>
                    </div>
                  )}
                  {firebase.isError && (
                    <div className={styles.mapLoadingOverlay}>
                      <Alert message="Map Unavailable" description={firebase.error} type="error" showIcon style={{ maxWidth: 280, fontSize: 12 }}
                        action={<Button size="small" type="primary" onClick={firebase.retry}>Retry</Button>} />
                    </div>
                  )}
                  {firebase.isLoaded && firebase.projectLocations.length === 0 && (
                    <div className={styles.mapLoadingOverlay}>
                      <Alert message="No Projects" type="warning" showIcon style={{ maxWidth: 280, fontSize: 12 }} />
                    </div>
                  )}
                  {shouldShowMap && (
                    <GoogleMapComponent
                      projectLocations={firebase.projectLocations}
                      selectedProject={selectedProject}
                      onProjectSelect={setSelectedProject}
                      mapType={mapView}
                      currentCheckIns={firebase.currentCheckIns}
                      employees={firebase.employees}
                      onMapLoad={handleMapLoad}
                    />
                  )}
                </div>
                {shouldShowMap && (
                  <div className={styles.mapStats}>
                    <Space size={16} wrap>
                      <span><EnvironmentOutlined style={{ color: '#1890ff', marginRight: 4 }} />{mapStats.totalProjects} Projects</span>
                      <span><AimOutlined style={{ color: '#52c41a', marginRight: 4 }} />{mapStats.projectsWithEmployees} Active</span>
                      <span><TeamOutlined style={{ color: '#722ed1', marginRight: 4 }} />{mapStats.totalCheckedIn} Checked-in</span>
                    </Space>
                  </div>
                )}
              </Card>
            </Col>

            {/* Recent Check-ins */}
            <Col xs={24}>
              <Card
                className={styles.activityCard}
                title={
                  <div className={styles.cardTitle}>
                    <ClockCircleOutlined /><span>Recent Check-ins</span>
                    <Badge count={recentCheckIns.length} style={{ backgroundColor: '#52c41a', marginLeft: 8, fontSize: 9 }} />
                  </div>
                }
                bodyStyle={{ padding: '12px 16px' }}
              >
                <List
                  className={styles.activityList}
                  dataSource={recentCheckIns.slice(0, 8)}
                  loading={recordsLoading}
                  locale={{ emptyText: 'No check-ins yet' }}
                  renderItem={record => (
                    <List.Item className={styles.activityItem}>
                      <div className={styles.activityItemContent}>
                        <div className={styles.activityItemHeader}>
                          <Avatar size={32} src={record.realImage} icon={<UserOutlined />} />
                          <div className={styles.activityInfo}>
                            <div className={styles.activityName}>{record.employeeName}</div>
                            <div className={styles.activityTime}>{record.checkIn}</div>
                          </div>
                          <Tag
                            color={record.status === 'Present' ? 'success' : record.status === 'Late' ? 'warning' : record.status === 'Incomplete' ? 'orange' : 'default'}
                            style={{ fontSize: 10, padding: '0 6px', margin: 0 }}
                          >{record.status}</Tag>
                        </div>
                        <div className={styles.activityLocation}>
                          <EnvironmentOutlined /><span>{record.normalizedLocation}</span>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default HrDashboard;